// @flow
import React from 'react';
import { Switch, Route, withRouter } from 'react-router-dom';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { Content } from '@finect/tabular-core/Content';
import CookiesCMP from '@finect/front-core/CookiesCMP';
import { isPreRelease } from '@finect/front-utils/isPreRelease';
import { Redirect } from '@finect/front-core/Routes';
import { Main } from '@finect/tabular-core/Main';
import { NotFound } from '@finect/tabular-components/NotFound';
import { Footer } from '@finect/tabular-components/Footer';
import { finect as theme } from '@finect/tabular-themes/finect';
import { OmniNav } from '@finect/tabular-omninav';
import { reset } from '@finect/tabular-utils/reset';
import { getUrlParameter } from '@finect/front-utils/getUrlParameter';
import { keyMap } from '@finect/front-utils/keyMap';
import { dataLayer } from '@finect/front-gtm';
import imported from 'react-imported-component';
import { getSuperPartnerBaseURL } from '@finect/front-utils/env';
import { InterstitialWithTargeting } from '@finect/tabular-components/Ads';
import { Mortgages, Landing, Companies, Professionals, Province, Help, Know, Faq, Advisor } from './scenes';

const PillModal = imported(() =>
  import(/* webpackChunkName: "TC-Pills" */ '@finect/tabular-components/Pills').then(module => module.PillModal));

/* eslint-disable */
const GlobalStyles = createGlobalStyle`
  ${reset}

  body > img {
    display: none;
  }
  
  [hidden] {
    display: none !important;
  }

  .app {
    // overflow-x: hidden;
    min-height: 100vh;
    text-rendering: optimizeLegibility;
  }
`;
/* eslint-enable */

const PILLS = {
  advisers: {
    slug: 'advisers-leads',
    text: 'financiero',
    url: 'advisers-leads/advisoryObjective'
  },
  mortgages: {
    slug: 'advisers-mortgages-anonymous',
    text: 'hipotecario',
    url: 'advisers-mortgages-anonymous/mortgageObjective'
  }
};

type Props = {
  user?: Object,
  location: Object,
  omniNavData?: Object
};

type State = {
  showModal: string | null,
  stepCTA: number,
  dataCTA: Object,
  loginAttemptEmail: string | null
};

class App extends React.Component<Props, State> {
  scrollProgress: number;

  state = {
    showModal: null,
    stepCTA: 0,
    dataCTA: {},
    loginAttemptEmail: null
  };

  toggleModal = (pill: string = 'advisers', stepCTA: number = 0, dataCTA: Object = {}, loginAttemptEmail: string | null = null) => {
    if (!this.state.showModal) {
      dataLayer({
        id: 'ProductLeads',
        type: 'addToCart',
        params: {
          model: {
            subtype: 'service',
            entity: {
              id: PILLS[pill].slug,
              alias: PILLS[pill].slug,
              name: `Asesoramiento ${PILLS[pill].text}`,
              commercializations: []
            }
          }
        }
      });
    }

    this.setState(state => ({
      showModal: state.showModal ? null : pill,
      stepCTA,
      dataCTA,
      loginAttemptEmail
    }));
  };

  handleKeydown = (event: any) => {
    if (event.keyCode === keyMap.ESCAPE) this.state.showModal && this.toggleModal();
  };

  componentDidUpdate(prevProps) {
    const { location } = this.props;
    const { location: prevLocation } = prevProps;

    typeof document !== 'undefined' && document.addEventListener('keydown', this.handleKeydown, false);

    if (prevLocation.pathname !== location.pathname) window.scrollTo(0, 0);
  }

  componentDidMount() {
    delete window.initialData;

    const step = Number(getUrlParameter('step', this.props.location.search) || 0);

    // seems like it's not causing problems for mortgages pill
    if (step) {
      window.history.replaceState({}, 'Asesores', window.location.pathname);

      const loginAttemptEmail =
        getUrlParameter('error', this.props.location.search) === 'true'
          ? getUrlParameter('email', this.props.location.search) || null
          : null;

      this.toggleModal('advisers', step, {}, loginAttemptEmail);
    }
  }

  render() {
    // if (!this.props.user.hasOwnProperty('isLoggedIn')) return null;
    const { showModal } = this.state;
    const { user, omniNavData } = this.props;

    return (
      <ThemeProvider theme={theme}>
        {user && <CookiesCMP user={user} cmpDomain={isPreRelease ? 'consent.finect.com' : 'cdn.privacy-mgmt.com'} />}

        <InterstitialWithTargeting />
        {showModal ? (
          <PillModal
            open={showModal}
            pill={`${getSuperPartnerBaseURL()}/pills/es_ES/finect/default/${PILLS[showModal].url}`}
            onLogin={() => {
              window.postMessage({ status: 'loggedIn' });
            }}
            growOnResize={false}
            onClose={this.toggleModal}
          />
        ) : null}

        <Main withLiveEvent>
          <GlobalStyles />
          <OmniNav omniNavData={omniNavData} finectApp="front-advisers" />
          <Content>
            <Switch>
              <Route
                exact
                path="/hipotecas/solicitar-hipoteca"
                render={props => <Mortgages toggleModal={() => this.toggleModal('mortgages')} {...props} />}
              />

              <Route exact path="/asesores" render={props => <Landing toggleModal={this.toggleModal} {...props} />} />
              <Route
                path="/asesores/asesor"
                render={props => <Advisor toggleModal={this.toggleModal} {...props} />}
              />
              <Route exact path="/asesores/faq" render={props => <Faq toggleModal={this.toggleModal} {...props} />} />
              <Route
                exact
                path="/asesores/encuentra-un-asesor/empresas"
                render={props => <Companies toggleModal={this.toggleModal} {...props} />}
              />
              <Route
                exact
                path="/asesores/encuentra-un-asesor/profesionales"
                render={props => <Professionals toggleModal={this.toggleModal} {...props} />}
              />
              <Route
                exact
                path="/asesores/encuentra-un-asesor/en/:province"
                render={props => <Province toggleModal={this.toggleModal} {...props} />}
              />
              <Route
                exact
                path="/asesores/encuentra-un-asesor/:page(\d+)"
                render={props => <Companies toggleModal={this.toggleModal} {...props} />}
              />
              <Redirect
                {...this.props}
                exact
                from="/asesores/encuentra-un-asesor"
                to="/asesores/encuentra-un-asesor/empresas"
                status={301}
                withSPA
              />

              <Route path="/asesores/ayuda" render={props => <Help toggleModal={this.toggleModal} {...props} />} />
              <Route path="/asesores/saber" render={props => <Know toggleModal={this.toggleModal} {...props} />} />
              <Route
                path="/asesores/beneficios"
                render={props => {
                  if (props.staticContext) {
                    props.staticContext.statusCode = 410;
                  }
                  return <NotFound status={410} actionText="Volver a asesores" redirectPath="/asesores"
                    message='Lo sentimos pero esta página ya no existe'
                    {...props} />;
                }}
              />

              <Route
                render={(props) => {
                  if (props.staticContext) {
                    props.staticContext.statusCode = 404;
                  }
                  return <NotFound actionText="Volver a asesores" redirectPath="/asesores" {...props} />;
                }}
              />
            </Switch>
          </Content>
          <Footer />
        </Main>
      </ThemeProvider>
    );
  }
}

export default withRouter(App);
